import i18n from '@/utils/i18n';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Action,
  SmartOrderAction,
  SmartOrderState,
} from './hooks/use-smart-order-reducer';
import { ApolloQueryResult } from '@apollo/client';
import {
  FlatList,
  StyleProp,
  StyleSheet,
  useWindowDimensions,
  View,
  ViewStyle,
} from 'react-native';
import { List } from '@/components/list';
import { Order_SmartOrderQuery } from '@/types';
import { SCREEN_PADDING } from '@/styles';
import { SMART_ORDER_LIST_ITEM_WIDTH } from './smart-order-list-item';
import { SmartOrderIndividualInput } from './smart-order-individual-input';
import { SmartOrderListItemLoading } from './smart-order-list-item-loading';
import { SmartOrderListOption, SmartOrderResult } from './smart-order-result';
import { useAdaptVariantPackToItemListOption } from '@/contexts/item-context-utils';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  dispatch: React.Dispatch<Action>;
  getSmartOrderResults: (
    items: string,
    isRestrictedSearch: boolean,
  ) => Promise<ApolloQueryResult<Order_SmartOrderQuery>>;
  listFooterStyle?: StyleProp<ViewStyle>;
  listItemStyle?: StyleProp<ViewStyle>;
  loading?: boolean;
  state: SmartOrderState;
}

export const SmartOrderResultsList: React.FC<Props> = ({
  dispatch,
  getSmartOrderResults,
  listFooterStyle,
  listItemStyle,
  loading,
  state,
}) => {
  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();
  const dimensions = useWindowDimensions();
  const { showErrorToast } = useToasts();

  const listRef = useRef<FlatList>(null);
  const [addItemLoading, setAddItemLoading] = useState(false);

  // Scroll to end when results are loading
  useEffect(() => {
    listRef.current?.scrollToEnd({
      animated: true,
    });
  }, [addItemLoading]);

  const listOptions: SmartOrderListOption[] = useMemo(
    () =>
      state.groupedResults.map((group, i) => ({
        metadata: {
          index: i,
          quantity: group.quantity,
          results: group.variantPacks.map((vp) =>
            adaptVariantPackToItemListOption(vp),
          ),
        },
        title: `"${group.searchTerm}"`,
        value: group.searchTerm,
      })),
    [adaptVariantPackToItemListOption, state.groupedResults],
  );

  const maxLoadingSkeletons = Math.ceil(
    dimensions.width / SMART_ORDER_LIST_ITEM_WIDTH,
  );

  return (
    <List<SmartOrderListOption>
      alwaysBounceVertical={false}
      data={listOptions}
      ItemSeparatorComponent={null}
      ListFooterComponent={
        !loading ? (
          <View style={listFooterStyle}>
            <View style={styles.itemRow}>
              <SmartOrderIndividualInput
                loading={addItemLoading}
                onSubmit={async (item) => {
                  setAddItemLoading(true);

                  try {
                    const { data } = await getSmartOrderResults(item, false);
                    dispatch({
                      payload: {
                        results:
                          data.getSmartOrderResults.length > 0
                            ? data.getSmartOrderResults
                            : [
                                {
                                  quantity: 0,
                                  search_term: item,
                                  variant_pack: null,
                                },
                              ],
                      },
                      type: SmartOrderAction.ADD_ITEM,
                    });
                  } catch {
                    showErrorToast();
                  }

                  setAddItemLoading(false);
                }}
                placeholder={i18n.t('add_another_item')}
              />
            </View>
            {addItemLoading ? (
              <View style={styles.itemRow}>
                {[...new Array(maxLoadingSkeletons)].map((_, i) => (
                  <SmartOrderListItemLoading key={i} />
                ))}
              </View>
            ) : null}
          </View>
        ) : null
      }
      listItemStyle={listItemStyle}
      listRef={listRef}
      renderMainContent={(option) =>
        loading ? (
          <View style={[styles.itemRow, styles.itemLoader]}>
            {[...new Array(maxLoadingSkeletons)].map((_, i) => (
              <SmartOrderListItemLoading key={i} />
            ))}
          </View>
        ) : (
          <SmartOrderResult
            dispatch={dispatch}
            getSmartOrderResults={getSmartOrderResults}
            option={option}
            state={state}
          />
        )
      }
    />
  );
};

const styles = StyleSheet.create({
  itemLoader: {
    marginHorizontal: SCREEN_PADDING,
  },
  itemRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
    marginTop: 8,
  },
});
